import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Box,
  Typography,
  IconButton,
  Tooltip,
  Popover,
} from '@mui/material';
import { Undo as UndoIcon, Schedule, AssignmentReturn, FastForward } from '@mui/icons-material';
import { DailyCollection, getCollectionStatus, formatDueDateDisplay, fmtCurrency } from './types';

interface CollectionTableProps {
  collections: DailyCollection[];
  viewType: 'daily' | 'weekly';
  onPayment: (collection: DailyCollection) => void;
  onReschedule: (collection: DailyCollection) => void;
  onUndoPayment: (collection: DailyCollection) => void;
  onReturn?: (collection: DailyCollection) => void;
  onAdvanceNext?: (collection: DailyCollection) => void;
}

export default function CollectionTable({
  collections,
  viewType,
  onPayment,
  onReschedule,
  onUndoPayment,
  onReturn,
  onAdvanceNext,
}: CollectionTableProps) {
  return (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 600 }}>Fecha</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Cliente</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Direccion</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Producto</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Cuota</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Monto</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Estado</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Acciones</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {collections.map((collection) => {
            const status = getCollectionStatus(collection);
            return (
              <TableRow key={`${collection.sale_id}-${collection.installment_number}-${collection.due_date}`}>
                <TableCell>{formatDueDateDisplay(collection.due_date)}</TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {collection.customer_surname} {collection.customer_name}
                  </Typography>
                </TableCell>
                <TableCell>{collection.customer_address}</TableCell>
                <TableCell>
                  {collection.notes?.startsWith('Productos: ')
                    ? collection.notes.replace('Productos: ', '')
                    : collection.product_name}
                </TableCell>
                <TableCell>
                  {collection.installment_number}/{collection.total_installments}
                  {(collection.overdue_count ?? 0) > 0 ? (
                    <OverdueChip collection={collection} />
                  ) : !collection.is_paid && (
                    <Chip
                      label="Al día"
                      size="small"
                      sx={{
                        ml: 0.5,
                        height: 20,
                        fontSize: '0.7rem',
                        backgroundColor: '#e8f5e9',
                        color: '#2e7d32',
                      }}
                    />
                  )}
                </TableCell>
                <TableCell>
                  {fmtCurrency(
                    collection.is_paid && (collection.total_paid_amount ?? 0) > collection.installment_amount
                      ? (collection.total_paid_amount ?? collection.installment_amount)
                      : collection.installment_amount
                  )}
                </TableCell>
                <TableCell>
                  <Chip
                    label={status.label}
                    size="small"
                    sx={{
                      backgroundColor: status.bgColor,
                      color: status.color,
                      fontWeight: 600,
                      fontSize: '0.75rem',
                    }}
                  />
                </TableCell>
                <TableCell>
                  <CollectionActions
                    collection={collection}
                    onPayment={onPayment}
                    onReschedule={onReschedule}
                    onUndoPayment={onUndoPayment}
                    onReturn={onReturn}
                    onAdvanceNext={onAdvanceNext}
                  />
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function CollectionActions({
  collection,
  onPayment,
  onReschedule,
  onUndoPayment,
  onReturn,
  onAdvanceNext,
}: {
  collection: DailyCollection;
  onPayment: (c: DailyCollection) => void;
  onReschedule: (c: DailyCollection) => void;
  onUndoPayment: (c: DailyCollection) => void;
  onReturn?: (c: DailyCollection) => void;
  onAdvanceNext?: (c: DailyCollection) => void;
}) {
  if (collection.is_returned) return null;

  // Botón para adelantar la próxima cuota cuando todavía hay impagas más adelante.
  const canAdvance = onAdvanceNext
    && collection.installment_number < collection.total_installments;
  const advanceButton = canAdvance ? (
    <Tooltip title="Adelantar próxima cuota">
      <IconButton size="small" onClick={() => onAdvanceNext!(collection)} sx={{ color: '#0EA5E9' }}>
        <FastForward />
      </IconButton>
    </Tooltip>
  ) : null;

  if (collection.is_paid) {
    return (
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        <Tooltip title="Deshacer Pago">
          <IconButton size="small" onClick={() => onUndoPayment(collection)}>
            <UndoIcon />
          </IconButton>
        </Tooltip>
        {advanceButton}
      </Box>
    );
  }

  if (collection.is_rescheduled) return null;

  const returnButton = onReturn ? (
    <Tooltip title="Devolver producto">
      <IconButton
        size="small"
        onClick={() => onReturn(collection)}
        sx={{ color: '#EF4444' }}
      >
        <AssignmentReturn />
      </IconButton>
    </Tooltip>
  ) : null;

  // Si la venta tiene un pago previo (caso prepago: el cliente cobró cuotas
  // futuras), permitir deshacer ese último cobro aunque la fila actual sea Pendiente.
  const undoLastButton = collection.last_sale_payment_id ? (
    <Tooltip title="Deshacer último cobro">
      <IconButton size="small" onClick={() => onUndoPayment(collection)} sx={{ color: '#6366F1' }}>
        <UndoIcon />
      </IconButton>
    </Tooltip>
  ) : null;

  if (collection.is_partial) {
    return (
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        <Button
          variant="contained"
          size="small"
          onClick={() => onPayment(collection)}
          sx={{
            background: 'linear-gradient(135deg, #3B82F6, #2563EB)',
            '&:hover': { background: 'linear-gradient(135deg, #2563EB, #1D4ED8)' },
          }}
        >
          Pagar saldo
        </Button>
        <Button
          variant="outlined"
          size="small"
          startIcon={<Schedule />}
          onClick={() => onReschedule(collection)}
        >
          Reprogramar
        </Button>
        {undoLastButton}
        {returnButton}
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
      <Button
        variant="contained"
        size="small"
        onClick={() => onPayment(collection)}
        sx={{
          background: 'linear-gradient(135deg, #10B981, #059669)',
          '&:hover': { background: 'linear-gradient(135deg, #059669, #047857)' },
        }}
      >
        Cobrar
      </Button>
      <Button
        variant="outlined"
        size="small"
        startIcon={<Schedule />}
        onClick={() => onReschedule(collection)}
      >
        Reprogramar
      </Button>
      {undoLastButton}
      {returnButton}
    </Box>
  );
}

function OverdueChip({ collection }: { collection: DailyCollection }) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const details = collection.overdue_details ?? [];

  return (
    <>
      <Chip
        label={`Debe ${collection.overdue_count}`}
        size="small"
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={{
          ml: 0.5,
          height: 20,
          fontSize: '0.65rem',
          fontWeight: 700,
          backgroundColor: 'rgba(239,68,68,0.15)',
          color: '#EF4444',
          cursor: 'pointer',
          '&:hover': { backgroundColor: 'rgba(239,68,68,0.25)' },
        }}
      />
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <Box sx={{ p: 2, minWidth: 240 }}>
          <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', mb: 1 }}>
            Cuotas adeudadas ({details.length})
          </Typography>
          {details.length > 0 ? details.map((d) => (
            <Box key={d.installment_number} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Typography sx={{ fontSize: '0.8rem' }}>
                Cuota {d.installment_number} — {formatDueDateDisplay(d.due_date)}
              </Typography>
              <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#EF4444', ml: 2 }}>
                {fmtCurrency(d.owed)}
              </Typography>
            </Box>
          )) : (
            <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>Sin detalle disponible</Typography>
          )}
        </Box>
      </Popover>
    </>
  );
}
